import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AuthService } from '../services/auth.service';
import { UserPreferencesService } from '../services/user-preferences.service';
import { SettingsModalService } from '../services/settings-modal.service';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

export interface YearFilter {
  year: number;
  enabled: boolean;
}

@Component({
  selector: 'app-settings',
  templateUrl: './settings.component.html',
  styleUrls: ['./settings.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule],
})
export class SettingsComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  // Use inject() function for dependency injection (Angular 19 pattern)
  public auth = inject(AuthService);
  private preferencesService = inject(UserPreferencesService);
  public modalService = inject(SettingsModalService);
  public isOpen$ = this.modalService.isOpen$;

  availableYears: YearFilter[] = [];
  loading = false;
  saveMessage = '';
  saveError = '';

  ngOnInit(): void {
    this.loadAvailableYears();
    this.loadUserPreferences();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async loadAvailableYears(): Promise<void> {
    try {
      this.loading = true;
      const years = await this.preferencesService.getAvailableYears();
      console.log('[Settings] Available years:', years);

      // Initialize year filters with all years enabled by default
      this.availableYears = years.map((year) => ({
        year,
        enabled: true,
      }));

      this.loading = false;
    } catch (error) {
      console.error('[Settings] Error loading available years:', error);
      this.saveError = 'Failed to load available years';
      this.loading = false;
    }
  }

  async loadUserPreferences(): Promise<void> {
    try {
      const preferences = await this.preferencesService.getUserPreferences();
      console.log('[Settings] User preferences:', preferences);

      if (preferences?.yearFilters && preferences.yearFilters.length > 0) {
        // Update the enabled state based on saved preferences
        this.availableYears = this.availableYears.map((yearFilter) => {
          const savedFilter = preferences.yearFilters.find(
            (f: YearFilter) => f.year === yearFilter.year
          );
          return {
            year: yearFilter.year,
            enabled: savedFilter ? savedFilter.enabled : true,
          };
        });
      }
    } catch (error) {
      console.error('[Settings] Error loading user preferences:', error);
      // Don't show error to user, just use defaults
    }
  }

  async savePreferences(): Promise<void> {
    try {
      this.loading = true;
      this.saveMessage = '';
      this.saveError = '';

      console.log('[Settings] Saving preferences:', this.availableYears);
      await this.preferencesService.saveYearFilters(this.availableYears);

      this.saveMessage = 'Preferences saved successfully!';
      this.loading = false;

      // Clear success message after 3 seconds
      setTimeout(() => {
        this.saveMessage = '';
      }, 3000);
    } catch (error: any) {
      console.error('[Settings] Error saving preferences:', error);
      console.error('[Settings] Error details:', {
        message: error?.message,
        code: error?.code,
        stack: error?.stack,
      });
      this.saveError = `Failed to save preferences: ${
        error?.message || 'Unknown error'
      }`;
      this.loading = false;
    }
  }

  toggleYear(year: number): void {
    const yearFilter = this.availableYears.find((y) => y.year === year);
    if (yearFilter) {
      yearFilter.enabled = !yearFilter.enabled;
    }
  }

  selectAllYears(): void {
    this.availableYears.forEach((y) => (y.enabled = true));
  }

  deselectAllYears(): void {
    this.availableYears.forEach((y) => (y.enabled = false));
  }

  get hasEnabledYears(): boolean {
    return this.availableYears.some((y) => y.enabled);
  }

  closeModal(): void {
    this.modalService.close();
  }
}
